import { FC, useEffect, useMemo } from 'react';

import { useRecoilValue } from 'recoil';

import GiftItemPreview from '@service/giftStore/components/GiftItemPreview';
import { trackGiftEvent } from '@shared/api/giftEventLog/giftEventLog.controller';
import { GiftChoiceUnitData } from '@shared/api/giftChoiceUnit/giftChoiceUnit.interface';
import {
  giftItemListForStoreDataAtomFamily,
  giftItemListForStoreStatusAtomFamily,
} from '@shared/api/giftItem/giftItem.atom';
import { useGiftItemListForStoreHook } from '@shared/api/giftItem/giftItem.hook';
import { GiftItemForStore } from '@shared/api/giftItem/giftItem.interface';
import { GiftItemInfoType } from '@shared/api/giftItemInfo/giftItemInfo.interface';
import { Image } from '@shared/api/media/media.interface';
import { Loading } from '@shared/components/Loading';
import { fileBucketUrl } from '@shared/utils/fileUtils';
import _ from 'lodash';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import giftPromStoreMain from 'src/assets/gift/gift-prom-store-main.png';

import { GiftCallHelperBox } from '../components/GiftCallHelperBox';
import GiftStoreFooter from '../components/GiftStoreFooter';

const GiftStoreMainPage: FC = function GiftStoreMainPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const paramsString = searchParams.toString();
  const bugoId = searchParams.get('bugoId');

  useEffect(() => {
    if (_.isNull(bugoId)) {
      navigate('/');
    } else {
      trackGiftEvent('store_view', bugoId);
    }
  }, [bugoId, navigate]);

  useGiftItemListForStoreHook(bugoId ?? '');
  const status = useRecoilValue(giftItemListForStoreStatusAtomFamily(bugoId ?? ''));
  const data = useRecoilValue(giftItemListForStoreDataAtomFamily(bugoId ?? ''));

  const getImageUrl = (item: GiftItemForStore): string => {
    const image = item.image || item.giftItemInfo?.image;
    if (image && typeof image === 'object' && 'url' in image) {
      return fileBucketUrl((image as Image).url, 'w400');
    }
    return '';
  };

  const getName = (item: GiftItemForStore): string => {
    return item.name || item.giftItemInfo?.name || '';
  };

  const getType = (item: GiftItemForStore): GiftItemInfoType => {
    return item.giftItemInfo?.type || GiftItemInfoType.Delivery;
  };

  const getTags = (item: GiftItemForStore): string[] => {
    return item.giftItemInfo?.tags || [];
  };

  const getIsSelectable = (item: GiftItemForStore): boolean => {
    return item.giftItemInfo?.isSelectable || false;
  };

  const getChoiceUnitCount = (item: GiftItemForStore): number => {
    const units = item.giftItemInfo?.choiceUnits;
    if (!units || !Array.isArray(units)) return 0;
    return units.filter(
      (u): u is GiftChoiceUnitData => typeof u === 'object' && u !== null,
    ).length;
  };

  const getBrandName = (item: GiftItemForStore): string | undefined => {
    const brand = item.giftItemInfo?.giftItemBrand;
    if (brand && typeof brand === 'object' && 'name' in brand) {
      return brand.name;
    }
    return undefined;
  };

  const getDisplayOrder = (item: GiftItemForStore): number => {
    if (item.order !== undefined) return item.order;
    if (item.giftItemInfo?.order !== undefined) return item.giftItemInfo.order;
    return Number.MAX_SAFE_INTEGER;
  };

  const sortedData = useMemo(() => {
    if (!data) return null;
    return [...data]
      .filter((item) => item.giftItemInfo?.isEnabled !== false)
      .sort((a, b) => getDisplayOrder(a) - getDisplayOrder(b));
  }, [data]);

  const itemCount = sortedData?.length ?? 0;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* 히어로 배너 */}
      <div>
        <img src={giftPromStoreMain} alt="답례품 안내" className="w-full" />
      </div>

      {/* 상품 목록 */}
      {status === 'success' && sortedData ? (
        sortedData.length > 0 ? (
          <div className="px-3 pb-4 pt-3">
            {/* 상품 그리드 */}
            <div className="grid grid-cols-2 gap-2.5">
              {sortedData.map((item, idx) => (
                <Link
                  key={`gift-item-link-${idx}`}
                  to={`/gift-store/item/${item._id}?${paramsString}`}
                  state={{ name: getName(item) }}
                >
                  <GiftItemPreview
                    image={getImageUrl(item)}
                    name={getName(item)}
                    priceRegular={item.priceRegular}
                    priceRetail={item.priceRetail}
                    type={getType(item)}
                    tags={getTags(item)}
                    isSelectable={getIsSelectable(item)}
                    choiceUnitCount={getChoiceUnitCount(item)}
                    brandName={getBrandName(item)}
                  />
                </Link>
              ))}
            </div>
          </div>
        ) : (
          <div className="center-box h-screen-15 flex-col gap-2 text-gray-500">
            <p>등록된 답례품이 없습니다.</p>
          </div>
        )
      ) : (
        <div className="center-box h-screen-15">
          <Loading />
        </div>
      )}
      <GiftCallHelperBox />
      <GiftStoreFooter />
    </div>
  );
};

export default GiftStoreMainPage;
